"use client";

import { useEffect, useSyncExternalStore } from 'react';

type Listener = () => void;

let currentNow = new Date();
let currentMinuteNow = truncateToMinute(currentNow);
const secondListeners = new Set<Listener>();
const minuteListeners = new Set<Listener>();
let intervalId: ReturnType<typeof setInterval> | null = null;

function truncateToMinute(date: Date): Date {
  const truncated = new Date(date);
  truncated.setSeconds(0, 0);
  return truncated;
}

function emit(listeners: Set<Listener>) {
  for (const listener of listeners) {
    listener();
  }
}

function tick() {
  const next = new Date();
  const nextMinute = truncateToMinute(next);
  currentNow = next;
  emit(secondListeners);
  if (nextMinute.getTime() !== currentMinuteNow.getTime()) {
    currentMinuteNow = nextMinute;
    emit(minuteListeners);
  }
}

function ensureTicker() {
  if (intervalId !== null) {
    return;
  }
  intervalId = setInterval(tick, 1_000);
}

function cleanupTicker() {
  if (
    secondListeners.size === 0 &&
    minuteListeners.size === 0 &&
    intervalId !== null
  ) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

function subscribeSeconds(listener: Listener) {
  secondListeners.add(listener);
  ensureTicker();
  return () => {
    secondListeners.delete(listener);
    cleanupTicker();
  };
}

function subscribeMinutes(listener: Listener) {
  minuteListeners.add(listener);
  ensureTicker();
  return () => {
    minuteListeners.delete(listener);
    cleanupTicker();
  };
}

function getSecondSnapshot() {
  return currentNow;
}

function getMinuteSnapshot() {
  return currentMinuteNow;
}

export function useNow(): Date {
  return useSyncExternalStore(
    subscribeMinutes,
    getMinuteSnapshot,
    getMinuteSnapshot,
  );
}

export function usePreciseNow(): Date {
  return useSyncExternalStore(
    subscribeSeconds,
    getSecondSnapshot,
    getSecondSnapshot,
  );
}

export function NowProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    ensureTicker();
    return () => {
      cleanupTicker();
    };
  }, []);
  return <>{children}</>;
}

